/**
 * #1106 Issue 18 — a single camera capture or gallery pick can deliver its
 * result twice on Android (the picker callback fires again after the activity
 * is restored), producing two identical attachments. Mirrors the fingerprint
 * window already used for inline images in `RichTextEditor.native.tsx`.
 */
import { useCallback, useRef } from 'react';

const DUPLICATE_PICK_WINDOW_MS = 10_000;

type PickedFileLike = {
  name?: string;
  size?: number;
  file?: unknown;
};

export function pickedFileFingerprint(file: PickedFileLike): string {
  const source = file.file;
  const uri =
    source && typeof source === 'object' && 'uri' in source
      ? String((source as { uri?: unknown }).uri ?? '')
      : '';
  return `${uri}|${file.size ?? ''}|${file.name ?? ''}`;
}

export type RecentFilePickGuard = <T extends PickedFileLike>(files: T[]) => T[];

/**
 * Returns a filter that drops picks matching one accepted within the last 10
 * seconds. Re-adding the same photo deliberately still works after the window.
 */
export function useRecentFilePickGuard(): RecentFilePickGuard {
  const recentPicksRef = useRef(new Map<string, number>());

  return useCallback(<T extends PickedFileLike>(files: T[]): T[] => {
    const now = Date.now();
    const recentPicks = recentPicksRef.current;

    for (const [fingerprint, pickedAt] of recentPicks) {
      if (now - pickedAt >= DUPLICATE_PICK_WINDOW_MS) {
        recentPicks.delete(fingerprint);
      }
    }

    return files.filter(file => {
      const fingerprint = pickedFileFingerprint(file);
      const pickedAt = recentPicks.get(fingerprint);
      if (pickedAt != null && now - pickedAt < DUPLICATE_PICK_WINDOW_MS) {
        return false;
      }
      recentPicks.set(fingerprint, now);
      return true;
    });
  }, []);
}
